import { Update } from '@reduxjs/toolkit'
import { Member } from '../models/Member'
import { Note } from '../models/Note'
import { UpdateBatch } from '../models/UpdateBatch'
import { Workplace } from '../models/Workplace'

export type sortProperties =
    | 'name'
    | 'arbejdssted'
    | 'status'
    | 'notes'
    | 'isOwnMember'
    | 'department'
    | 'birthday'
    | 'age'
    | 'date'
    | 'favorite'
    | 'created'
    | 'updateType'
    | 'updateProfile'
    | 'updateUpdated'
    | 'numberOfMembers'
    | 'isRead'
export type sortDirectionType = 'ascending' | 'descending '

export type sortProperty = {
    direction: sortDirectionType
    property: sortProperties
}

export interface ISortName<T> {
    property: Extract<keyof T, string>
}

function compareName<T>(objectA: T, objectB: T, sorter: ISortName<T>): number {
    if (objectA[sorter.property] < objectB[sorter.property]) {
        return -1
    }

    if (objectA[sorter.property] > objectB[sorter.property]) {
        return 1
    }
    return 0
}

function compareMemberOnUpdateName(a: UpdateBatch, b: UpdateBatch): number {
    if (a.member.fullName < b.member.fullName) {
        return -1
    }

    if (a.member.fullName > b.member.fullName) {
        return 1
    }
    return 0
}

function compareProfileOnUpdateName(a: UpdateBatch, b: UpdateBatch): number {
    if (a.profile.fullName < b.profile.fullName) {
        return -1
    }

    if (a.profile.fullName > b.profile.fullName) {
        return 1
    }
    return 0
}

function sortByNoteFavorite(notes: Note[]): Note[] {
    return notes.sort((a: Note, b: Note) => {
        if (a.isFavorite) return -1
        if (b.isFavorite) return 1
        return 0
    })
}

function sortByDate(members: Member[]): Member[] {
    return members.sort(
        (a: Member, b: Member) => b.birthday.getTime() - a.birthday.getTime()
    )
}

function sortByNoteDate(notes: Note[]): Note[] {
    return notes.sort(
        (a: Note, b: Note) => b.updated.getTime() - a.updated.getTime()
    )
}

function sortByUpdateCreatedDate(updates: UpdateBatch[]): UpdateBatch[] {
    return updates.sort(
        (a: UpdateBatch, b: UpdateBatch) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
    )
}

function sortByUpdateUpdatedDate(updates: UpdateBatch[]): UpdateBatch[] {
    return updates.sort(
        (a: UpdateBatch, b: UpdateBatch) =>
            new Date(b.updated).getTime() - new Date(a.updated).getTime()
    )
}

function compareAge(a: Member, b: Member): number {
    if (a.age < b.age) {
        return -1
    }
    if (a.age > b.age) {
        return 1
    }
    return 0
}

function compareWorkplaces(a: Member, b: Member): number {
    if (
        a.primaryWorkPlace.name.toLowerCase() <
        b.primaryWorkPlace.name.toLowerCase()
    ) {
        return -1
    }
    if (
        a.primaryWorkPlace.name.toLowerCase() >
        b.primaryWorkPlace.name.toLowerCase()
    ) {
        return 1
    }
    return 0
}

function compareStatus(a: Member): number {
    if (a.status === 'AfventerGodkendelse') {
        return -1
    }
    if (a.status === 'IkkeOpdateret ') {
        return 1
    }
    return 0
}

function compareNotes(a: Member | Workplace, b: Member | Workplace): number {
    if (a.numberOfNotes < b.numberOfNotes) {
        return -1
    }
    if (a.numberOfNotes > b.numberOfNotes) {
        return 1
    }
    return 0
}

function compareIsRead(a: Note): number {
    if (!a.isRead) {
        return -1
    }
    if (a.isRead) {
        return 1
    }
    return 0
}

function compareNumberOfMembersOnWorkplace(a: Workplace, b: Workplace): number {
    if (a.numberOfMembers < b.numberOfMembers) {
        return -1
    }
    if (a.numberOfMembers > b.numberOfMembers) {
        return 1
    }
    return 0
}

function compareIsOwnMember(a: Member): number {
    if (a.isOwnMember) {
        return -1
    }
    if (!a.isOwnMember) {
        return 1
    }
    return 0
}

function compareDepartments(a: Member, b: Member): number {
    if (
        a.department.name &&
        b.department.name &&
        a.department.name.toLowerCase() < b.department.name.toLowerCase()
    ) {
        return -1
    }
    if (
        a.department.name &&
        b.department.name &&
        a.department.name.toLowerCase() > b.department.name.toLowerCase()
    ) {
        return 1
    }
    return 0
}

export const sortWorkplacesBy = (
    workplaces: Workplace[],
    sort: sortProperty
): Workplace[] => {
    let sorted = [...workplaces]

    switch (sort.property) {
        case 'name':
            sorted = sorted.sort((a, b) =>
                compareName(a, b, { property: 'name' })
            )
            break
        case 'numberOfMembers':
            sorted = sorted.sort(compareNumberOfMembersOnWorkplace)
            break
        case 'notes':
            sorted = sorted.sort(compareNotes)
            break

        default:
            break
    }

    if (sort.direction === 'descending ') sorted = sorted.reverse()

    return sorted
}

export const sortUpdatesBy = (
    updates: UpdateBatch[],
    sort: sortProperty
): UpdateBatch[] => {
    let sorted = [...updates]

    switch (sort.property) {
        case 'status': {
            const sorted1 = sorted.filter(
                (item) => item.status === 'FaerdigBehandlet'
            )
            const sorted2 = sorted.filter(
                (item) => item.status === 'UnderOpdatering'
            )
            sorted = [...sorted1, ...sorted2]
            break
        }

        case 'created':
            sorted = sortByUpdateCreatedDate(sorted)
            break
        case 'name':
            sorted = sorted.sort((a, b) => compareMemberOnUpdateName(a, b))
            break
        case 'updateType':
            sorted = sorted.sort((a, b) =>
                compareName(a, b, { property: 'type' })
            )
            break
        case 'updateProfile':
            sorted = sorted.sort((a, b) => compareProfileOnUpdateName(a, b))
            break
        case 'updateUpdated':
            sorted = sortByUpdateUpdatedDate(sorted)
            break

        default:
            break
    }

    if (sort.direction === 'descending ') sorted = sorted.reverse()

    return sorted
}

export const sortNotesByDateAndIsRead = (
    notes: Note[],
    reverse?: boolean
): Note[] => {
    // Find all is read.
    let isRead = notes.filter((n) => n.isRead)
    let isNotRead = notes.filter((n) => !n.isRead)
    isRead = sortByNoteDate(isRead)
    isNotRead = sortByNoteDate(isNotRead)

    return !reverse ? [...isNotRead, ...isRead] : [...isRead, ...isNotRead]
}

export const sortNotesBy = (notes: Note[], sort: sortProperty): Note[] => {
    let sortedNotes = [...notes]

    switch (sort.property) {
        case 'isRead':
            sortedNotes = sortedNotes.sort(compareIsRead)

            sortedNotes = sortNotesByDateAndIsRead(
                sortedNotes,
                sort.direction === 'descending '
            )

            break
        case 'name':
            sortedNotes = sortedNotes.sort((a, b) =>
                compareName(a, b, { property: 'name' })
            )
            break
        case 'date':
            sortedNotes = sortByNoteDate(sortedNotes)
            break

        case 'favorite':
            sortedNotes = sortByNoteFavorite(sortedNotes).sort()
            break

        default:
            break
    }

    if (sort.direction === 'descending ' && sort.property !== 'isRead')
        sortedNotes = sortedNotes.reverse()

    return sortedNotes
}

export const sortBy = (members: Member[], sort: sortProperty): Member[] => {
    let sortedMembers = [...members]

    switch (sort.property) {
        case 'name':
            sortedMembers = sortedMembers.sort((a, b) =>
                compareName(a, b, { property: 'fullName' })
            )
            break

        case 'arbejdssted':
            sortedMembers = sortedMembers.sort(compareWorkplaces)
            break

        case 'status':
            sortedMembers = sortedMembers.sort(compareStatus)
            break

        case 'notes':
            sortedMembers = sortedMembers.sort(compareNotes)
            break

        case 'isOwnMember':
            sortedMembers = sortedMembers.sort(compareIsOwnMember)
            break

        case 'department':
            sortedMembers = sortedMembers.sort(compareDepartments)
            break

        case 'birthday':
            sortedMembers = sortByDate(sortedMembers)
            break
        case 'age':
            sortedMembers = sortedMembers.sort(compareAge)
            break

        default:
            break
    }

    if (sort.direction === 'descending ')
        sortedMembers = sortedMembers.reverse()

    return sortedMembers
}
